import React from "react";
import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Activity, Loader } from "lucide-react";

export const SENSOR_CHART_INTERVALS = [
  { value: 10, label: "10 min" },
  { value: 30, label: "30 min" },
  { value: 60, label: "1 hr" },
];

const IST_TIME_ZONE = "Asia/Kolkata";
const BAR_COLOR = "#2a78d6";
const GRID_COLOR = "#e2e8f0";
const AXIS_TEXT_COLOR = "#64748b";
// The range slider opens on the most recent day of windows.
const MINUTES_PER_DAY = 24 * 60;

const formatWith = (options) => {
  const formatter = new Intl.DateTimeFormat("en-IN", {
    timeZone: IST_TIME_ZONE,
    ...options,
  });
  return (seconds) => formatter.format(new Date(seconds * 1000));
};

const formatTick = formatWith({
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
});
const formatDay = formatWith({ day: "2-digit", month: "short", year: "numeric" });
const formatClock = formatWith({ hour: "2-digit", minute: "2-digit", hour12: true });
const formatClockWithSeconds = formatWith({
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
});

const formatReading = (value, unit) =>
  unit ? `${value} ${unit}` : String(value);

const ChartTooltip = ({ active, payload, sensorLabel, unit }) => {
  const point = active && payload?.[0]?.payload;
  if (!point) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg">
      <p className="text-sm font-bold text-slate-900">
        {formatReading(point.value, unit)}
      </p>
      <p className="mt-0.5 font-medium text-slate-600">{sensorLabel}</p>
      <p className="mt-1.5 text-slate-500">
        {formatDay(point.window_start)}, {formatClock(point.window_start)} –{" "}
        {formatClock(point.window_end)}
      </p>
      <p className="text-slate-500">
        Reading at {formatClockWithSeconds(point.reading_timestamp)}
      </p>
    </div>
  );
};

const SensorBarChart = ({ series, sensorLabel, intervalMinutes }) => {
  const points = series.points || [];
  const windowsPerDay = MINUTES_PER_DAY / intervalMinutes;
  const showBrush = points.length > windowsPerDay;
  const title = series.unit ? `${sensorLabel} (${series.unit})` : sensorLabel;

  return (
    <div className="rounded-xl border border-slate-100 bg-white p-3 shadow-sm">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="text-sm font-bold text-slate-800">{title}</h4>
        <span className="text-xs text-slate-500">
          {points.length} window{points.length === 1 ? "" : "s"}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={showBrush ? 290 : 250}>
        <BarChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke={GRID_COLOR} />
          <XAxis
            dataKey="window_start"
            tickFormatter={formatTick}
            minTickGap={40}
            tick={{ fill: AXIS_TEXT_COLOR, fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: "#cbd5e1" }}
          />
          <YAxis
            width={56}
            tick={{ fill: AXIS_TEXT_COLOR, fontSize: 11 }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            cursor={{ fill: "#f1f5f9" }}
            content={<ChartTooltip sensorLabel={sensorLabel} unit={series.unit} />}
          />
          <Bar
            dataKey="value"
            fill={BAR_COLOR}
            radius={[4, 4, 0, 0]}
            maxBarSize={24}
            isAnimationActive={false}
          />
          {showBrush && (
            <Brush
              key={`${intervalMinutes}-${points.length}`}
              dataKey="window_start"
              height={24}
              stroke="#94a3b8"
              travellerWidth={8}
              startIndex={Math.max(0, points.length - windowsPerDay)}
              tickFormatter={formatTick}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const SensorHistoryChart = ({
  chart,
  loading,
  hasSearched,
  intervalMinutes,
  onIntervalChange,
  getSensorLabel,
}) => {
  const series = chart?.series || [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          Each bar is the last reading in its time window (IST).
        </p>
        <div
          className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1"
          role="group"
          aria-label="Time window"
        >
          {SENSOR_CHART_INTERVALS.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={intervalMinutes === option.value}
              onClick={() => onIntervalChange(option.value)}
              disabled={loading}
              className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors disabled:opacity-60 ${
                intervalMinutes === option.value
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex h-[250px] items-center justify-center gap-2 rounded-lg border border-slate-100 bg-slate-50 text-sm text-slate-500">
          <Loader className="h-4 w-4 animate-spin" />
          Loading chart...
        </div>
      ) : !hasSearched ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-12 text-center text-sm text-slate-500">
          Choose filters and click Search to plot readings.
        </div>
      ) : series.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-12 text-center">
          <Activity className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="font-medium text-slate-700">No readings to plot</p>
          <p className="mt-1 text-sm text-slate-500">
            Try another sensor or date range.
          </p>
        </div>
      ) : (
        series.map((item) => (
          <SensorBarChart
            key={item.sensor_id}
            series={item}
            sensorLabel={getSensorLabel(item.sensor_id)}
            intervalMinutes={chart.interval_minutes}
          />
        ))
      )}
    </div>
  );
};

export default SensorHistoryChart;
